import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import AuthService from '../services/AuthService';
import toast from 'react-hot-toast';

const ProtectedRoute = ({ children }) => {
  const { state, actions } = useUser();
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const location = useLocation();
  const hasCheckedAuth = useRef(false);
  const lastPathname = useRef(location.pathname);

  useEffect(() => {
    // Only check auth on mount or when pathname changes
    const pathnameChanged = lastPathname.current !== location.pathname;
    lastPathname.current = location.pathname;

    // Skip if we've already checked and user is authenticated and pathname hasn't changed
    if (hasCheckedAuth.current && state.user && !pathnameChanged) {
      return;
    }

    const checkAuth = async () => {
      try {
        // First check if token exists and is not expired locally
        if (!AuthService.isAuthenticated()) {
          setLoading(false);
          setIsAuthenticated(false);
          hasCheckedAuth.current = true;
          return;
        }

        // If user is already in context and token is valid, skip backend check
        // This prevents unnecessary API calls on every render
        if (state.user && AuthService.isAuthenticated()) {
          setIsAuthenticated(true);
          setLoading(false);
          setIsInitialLoad(false);
          hasCheckedAuth.current = true;
          return;
        }

        // Only validate with backend if we don't have a user in context
        // This should only happen on initial load or after a page refresh
        if (!state.user) {
          const response = await AuthService.validateToken();
          
          if (response.valid && response.user) {
            // Update user context with fresh data
            // Ensure leagueId is preserved (important for student account detection)
            actions.setUser({
              ...response.user,
              leagueId: response.user.leagueId || null
            });
            actions.setOnboarded(true);
            setIsAuthenticated(true);
          } else {
            // Token is invalid, logout silently on initial load
            AuthService.logout();
            actions.setUser(null);
            actions.setOnboarded(false);
            setIsAuthenticated(false);
            
            // Only show error message if this is not the initial page load
            if (!isInitialLoad) {
              toast.error('Session expired. Please login again.');
            }
          }
        } else {
          // User exists in context and token is valid
          setIsAuthenticated(true);
        }
      } catch (error) {
        // Only logout on actual auth errors, not network errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          AuthService.logout();
          actions.setUser(null);
          actions.setOnboarded(false);
          setIsAuthenticated(false);
          
          // Only show error message if this is not the initial page load
          if (!isInitialLoad) {
            toast.error('Session expired. Please login again.');
          }
        } else {
          // For other errors (network issues), keep user logged in if token is valid locally
          if (AuthService.isAuthenticated() && state.user) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        }
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
        hasCheckedAuth.current = true;
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Only re-check when route changes

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
